/**
 * Shop API client + cart/wishlist context.
 *
 * Guest cart uses a UUID stored in localStorage as `cart_token`.
 * Logged-in cart is keyed by email on the backend.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ============================ fetch helper ================================ */
const authHeaders = () => {
  const h = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  const cartToken = localStorage.getItem('cart_token');
  if (token)     h.Authorization  = `Bearer ${token}`;
  if (cartToken) h['X-Cart-Token'] = cartToken;
  return h;
};

export const shopApi = {
  async listProducts(params = {}) {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    );
    const r = await fetch(`${API}/api/shop/products?${q}`);
    return r.json();
  },
  async getProduct(slug) {
    const r = await fetch(`${API}/api/shop/products/${slug}`);
    if (!r.ok) throw new Error('not_found');
    return r.json();
  },
  async getHomepage() {
    const r = await fetch(`${API}/api/shop/homepage`);
    return r.json();
  },
  async addToCart(product_id, size, color, qty = 1) {
    const r = await fetch(`${API}/api/shop/cart/add`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ product_id, size, color, qty }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.detail || 'Could not add to cart');
    if (data.cart_token) localStorage.setItem('cart_token', data.cart_token);
    return data;
  },
  async getCart() {
    const r = await fetch(`${API}/api/shop/cart`, { headers: authHeaders() });
    return r.json();
  },
  async updateCartItem(product_id, size, color, qty) {
    const r = await fetch(`${API}/api/shop/cart/item`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ product_id, size, color, qty }),
    });
    return r.json();
  },
  async removeFromCart(product_id, size, color) {
    const q = new URLSearchParams({ product_id, ...(size ? { size } : {}), ...(color ? { color } : {}) });
    const r = await fetch(`${API}/api/shop/cart/item?${q}`, { method: 'DELETE', headers: authHeaders() });
    return r.json();
  },
  async toggleWishlist(slug) {
    const r = await fetch(`${API}/api/shop/wishlist/toggle`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ slug }),
    });
    if (!r.ok) throw new Error('auth_required');
    return r.json();
  },
  async getWishlist() {
    const r = await fetch(`${API}/api/shop/wishlist`, { headers: authHeaders() });
    if (!r.ok) return { products: [], product_slugs: [] };
    return r.json();
  },
  async subscribeNewsletter(email) {
    const r = await fetch(`${API}/api/shop/newsletter`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return r.json();
  },
  async createOrder(payload) {
    const r = await fetch(`${API}/api/shop/orders/create`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error((await r.json()).detail || 'order_failed');
    return r.json();
  },
  async verifyOrder(payload) {
    const r = await fetch(`${API}/api/shop/orders/verify`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return r.json();
  },
  async getOrder(order_id) {
    const r = await fetch(`${API}/api/shop/orders/${order_id}`, { headers: authHeaders() });
    return r.json();
  },
  async myOrders() {
    const r = await fetch(`${API}/api/shop/orders`, { headers: authHeaders() });
    return r.ok ? r.json() : { orders: [] };
  },
};

export const INR = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

/* ============================ Shop context ================================ */
const ShopCtx = createContext({ cart: { items: [], subtotal: 0 }, wishlistSlugs: [], refreshCart: () => {}, refreshWishlist: () => {} });

export const ShopProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [wishlistSlugs, setWishlist] = useState([]);

  const refreshCart = useCallback(async () => {
    const { cart } = await shopApi.getCart();
    setCart(cart || { items: [], subtotal: 0 });
  }, []);
  const refreshWishlist = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setWishlist([]); return; }
    const { product_slugs } = await shopApi.getWishlist();
    setWishlist(product_slugs || []);
  }, []);

  useEffect(() => {
    const refresh = () => { refreshCart(); refreshWishlist(); };
    refresh();
    window.addEventListener('auth-changed', refresh);
    return () => window.removeEventListener('auth-changed', refresh);
  }, [refreshCart, refreshWishlist]);

  return (
    <ShopCtx.Provider value={{ cart, wishlistSlugs, refreshCart, refreshWishlist, setCart }}>
      {children}
    </ShopCtx.Provider>
  );
};

export const useShop = () => useContext(ShopCtx);
